import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Collapse,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useNotification } from '../../../hooks';
import { useAlertBadge } from '../../../hooks/useAlertBadge';
import { usePermissionsContext } from '../../../providers/PermissionsProvider';
import {
  getAccessibleNavigationItems,
  getAccessibleFooterNavigationItems,
} from '../../../constants/navigationData';
import { sidebarContentStyles, menuItemStyles } from './SidebarContent.styles';
import { EXTERNAL_APPS } from '../../../config/externalApps';
import { openExternalApp } from '../../../utils/externalAppSSO';

const SidebarContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [expandedItems, setExpandedItems] = useState({});

  const { logout, isOperationInProgress, user } = useAuth();
  const { hasPermission, initialized } = usePermissionsContext();
  const { unreadCount } = useAlertBadge();

  // userRole opsiyonel — nav item'larında `rolesAllowed: ['ROOT']` gibi
  // role bazlı ek kısıt için kullanılıyor (Prim Yönetimi alt menüleri
  // ROOT haricinde gizli kalsın diye).
  const navigationItems = initialized ? getAccessibleNavigationItems(hasPermission, user?.role) : [];
  const footerNavigationItems = initialized ? getAccessibleFooterNavigationItems(hasPermission) : [];

  const [contextMenu, setContextMenu] = useState(null);

  const handleLogout = useCallback(async () => {
    try {
      const result = await logout();
      
      if (result.success) {
        showNotification(
          result.message || t('auth.logout.success', 'Başarıyla çıkış yaptınız'),
          'success'
        );
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 500);
      } else {
        showNotification(
          t('auth.logout.error', 'Çıkış sırasında bir hata oluştu'),
          'warning'
        );
      }
    } catch (error) {
      showNotification(
        t('auth.logout.error', 'Çıkış sırasında bir hata oluştu'),
        'error'
      );
    }
  }, [logout, showNotification, t, navigate]);

  const handleMenuClick = useCallback(async (item) => {
    if (isOperationInProgress) {
      return;
    }

    try {
      if (item.action === 'logout') {
        await handleLogout();
      } else if (item.externalApp) {
        // Dis uygulama (Arge Net vb.) — SSO token al, yeni sekmede ac.
        // Popup blocker'i bypass etmek icin helper icinde `window.open`
        // ilk anda cagriliyor; async token gelince URL set ediliyor.
        openExternalApp(item.externalApp, showNotification);
      } else if (item.subItems) {
        // Accordion davranışı: Sadece bir ana kategori açık olabilir
        setExpandedItems(prev => {
          const newExpandedItems = {};
          
          // Eğer tıklanan öğe zaten açıksa, kapat
          if (prev[item.key]) {
            return newExpandedItems;
          }
          
          // Eğer tıklanan öğe kapalıysa, diğerlerini kapat ve bunu aç
          newExpandedItems[item.key] = true;
          return newExpandedItems;
        });
      } else if (item.path) {
        navigate(item.path);
      }
    } catch (error) {
      showNotification(
        t('common.errors.actionFailed', 'İşlem gerçekleştirilemedi'),
        'error'
      );
    }
  }, [isOperationInProgress, handleLogout, navigate, showNotification, t]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Not: openExternalApp modul-level import; deps'e eklemeye gerek yok.

  // Dis uygulama menu item'lari — sidebar'da "Diger Uygulamalar" bolumune
  // basildiginda SSO ile yeni sekmede acilir. Config: src/config/externalApps.js.
  const externalAppItems = React.useMemo(
    () => (EXTERNAL_APPS || []).map((app) => ({
      key: `ext-${app.code}`,
      // label i18n key yerine ham metin geciyoruz — item'da label direkt gosterilir.
      // renderMenuItem `t(labelKey)` bekliyorsa asagida customLabel field'i ile
      // yakalanir.
      labelKey: null,
      customLabel: app.name,
      // icon: RocketLaunchIcon yok cunku logo kullaniyoruz — renderMenuItem'da
      // externalApp.logo varsa img render edilir.
      icon: null,
      color: app.color || 'primary',
      externalApp: app,       // handleMenuClick bunu tanir, openExternalApp cagirir
      requiresPermission: false,
    })),
    [],
  );

  const handleContextMenu = useCallback((e, path) => {
    e.preventDefault();
    e.stopPropagation();
    if (path) setContextMenu({ x: e.clientX, y: e.clientY, path });
  }, []);

  const handleCloseContextMenu = useCallback(() => setContextMenu(null), []);

  const handleOpenInNewTab = useCallback(() => {
    if (contextMenu?.path) {
      const url = contextMenu.path.startsWith('http') ? contextMenu.path : `${window.location.origin}${contextMenu.path}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    handleCloseContextMenu();
  }, [contextMenu?.path, handleCloseContextMenu]);

  const renderMenuItem = useCallback((item) => {
    const IconComponent = item.icon;
    const isActive = item.path === '/'
      ? location.pathname === '/'
      : item.path && location.pathname.startsWith(item.path);

    const isLogoutItem = item.action === 'logout';
    const isItemLoading = isLogoutItem && isOperationInProgress;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedItems[item.key];

    return (
      <React.Fragment key={item.key}>
        <ListItem
          onClick={() => handleMenuClick(item)}
          onContextMenu={item.path ? (e) => handleContextMenu(e, item.path) : undefined}
          sx={{
            ...menuItemStyles.listItem(isActive),
            cursor: isItemLoading ? 'not-allowed' : 'pointer',
            opacity: isItemLoading ? 0.7 : 1,
            transition: 'opacity 0.2s ease',
          }}
          disabled={isItemLoading}
          role="menuitem"
          aria-label={t(item.labelKey)}
          tabIndex={isItemLoading ? -1 : 0}
        >
          <ListItemIcon sx={menuItemStyles.listItemIcon}>
            {isItemLoading ? (
              <CircularProgress
                size={20}
                aria-label={t('auth.logout.loading', 'Çıkış yapılıyor...')}
              />
            ) : item.key === 'alerts' ? (
              <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
                <IconComponent />
              </Badge>
            ) : item.externalApp?.logo ? (
              // Dis uygulama karti — public logo goster (MUI icon yerine).
              // Logo boyutu MUI icon'larindan daha buyuk (36) — kullanici
              // bunu marka olarak gormeli, tanidik olmali. Sagdan mr:1.5
              // — aksi halde isim logoya yapisik goruluyordu.
              <Box
                component="img"
                src={item.externalApp.logo}
                alt={item.externalApp.name}
                sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0.5, mr: 2 }}
              />
            ) : IconComponent ? (
              <IconComponent />
            ) : null}
          </ListItemIcon>

          <ListItemText
            primary={
              isItemLoading
                ? t('auth.logout.loading', 'Çıkış yapılıyor...')
                : (item.customLabel || t(item.labelKey))
            }
            primaryTypographyProps={{
              ...menuItemStyles.listItemText(isActive),
              'aria-hidden': isItemLoading ? 'true' : 'false'
            }}
          />
          
          {hasSubItems && !isItemLoading && (
            <Box sx={{ ml: 'auto' }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          )}

          {item.badge && !isItemLoading && !hasSubItems && (
            <Chip
              label={item.badge}
              size="small"
              color={item.color || 'primary'}
              sx={menuItemStyles.chip}
              aria-label={`${t(item.labelKey)} - ${item.badge} items`}
            />
          )}
        </ListItem>
        
        {hasSubItems && (
          <Collapse in={isExpanded} timeout={200} unmountOnExit>
            <List component="div" disablePadding sx={{ mt: 0.5 }}>
              {item.subItems.map((subItem) => {
                const isSubItemActive = subItem.path && location.pathname === subItem.path;
                
                return (
                                                        <ListItem
                     key={subItem.key}
                     onClick={() => navigate(subItem.path)}
                     onContextMenu={subItem.path ? (e) => handleContextMenu(e, subItem.path) : undefined}
                     sx={{
                       ...menuItemStyles.subItem(isSubItemActive),
                       pl: 4,
                     }}
                     role="menuitem"
                     aria-label={t(subItem.labelKey)}
                   >
                     {subItem.icon && (
                       <ListItemIcon sx={menuItemStyles.subItemIcon}>
                         <subItem.icon />
                       </ListItemIcon>
                     )}
                     <ListItemText
                       primary={t(subItem.labelKey)}
                       primaryTypographyProps={menuItemStyles.subItemText(isSubItemActive)}
                     />
                    
                                         {subItem.subItems && subItem.subItems.length > 0 && (
                       <Collapse in={expandedItems[subItem.key]} timeout={200} unmountOnExit>
                         <List component="div" disablePadding sx={{ mt: 0.5 }}>
                          {subItem.subItems.map((nestedSubItem) => {
                            const isNestedSubItemActive = nestedSubItem.path && location.pathname.startsWith(nestedSubItem.path);
                            
                            return (
                                                                                        <ListItem
                               key={nestedSubItem.key}
                               onClick={() => navigate(nestedSubItem.path)}
                               onContextMenu={nestedSubItem.path ? (e) => handleContextMenu(e, nestedSubItem.path) : undefined}
                               sx={{
                                 ...menuItemStyles.subItem(isNestedSubItemActive),
                                 pl: 6,
                               }}
                               role="menuitem"
                               aria-label={t(nestedSubItem.labelKey)}
                             >
                               {nestedSubItem.icon && (
                                 <ListItemIcon sx={menuItemStyles.subItemIcon}>
                                   <nestedSubItem.icon />
                                 </ListItemIcon>
                               )}
                               <ListItemText
                                 primary={t(nestedSubItem.labelKey)}
                                 primaryTypographyProps={menuItemStyles.subItemText(isNestedSubItemActive)}
                               />
                             </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  }, [location.pathname, handleMenuClick, handleContextMenu, isOperationInProgress, t, expandedItems, navigate]);
  if (!initialized) {
    return (
      <Box sx={sidebarContentStyles.container}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px' 
        }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={sidebarContentStyles.container} role="navigation" aria-label="Main navigation">
      <Box sx={sidebarContentStyles.scrollArea}>
        <Box sx={sidebarContentStyles.navigationContainer}>
          <List
            sx={sidebarContentStyles.navigationList}
            role="menu"
            aria-label="Primary navigation"
          >
            {navigationItems.map(item => renderMenuItem(item))}
          </List>
        </Box>

        {/* Dis uygulamalar bolumu — Arge Net ve gelecek diger app'ler. Tanim:
            src/config/externalApps.js. Bolum sadece EXTERNAL_APPS bosdegilse
            gorunur. Sistem bolumunun hemen ustunde konumlanir. */}
        {externalAppItems.length > 0 && (
          <Box sx={sidebarContentStyles.systemSection}>
            <Divider sx={sidebarContentStyles.sectionDivider} />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={sidebarContentStyles.sectionLabel}
              role="heading"
              aria-level="3"
            >
              {t('navigation.externalApps', 'Diğer Uygulamalar')}
            </Typography>

            <List
              sx={sidebarContentStyles.navigationList}
              role="menu"
              aria-label="External applications"
            >
              {externalAppItems.map(item => renderMenuItem(item))}
            </List>
          </Box>
        )}

        <Box sx={sidebarContentStyles.systemSection}>
          <Divider sx={sidebarContentStyles.sectionDivider} />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={sidebarContentStyles.sectionLabel}
            role="heading"
            aria-level="3"
          >
            {t('navigation.system', 'System')}
          </Typography>

          <List
            sx={sidebarContentStyles.navigationList}
            role="menu"
            aria-label="System navigation"
          >
            {footerNavigationItems.map(item => renderMenuItem(item))}
          </List>
        </Box>
      </Box>
            <Box sx={sidebarContentStyles.footer}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={sidebarContentStyles.footerText}
          role="contentinfo"
        >
          AI Thinks v2.0.1
        </Typography>
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
        MenuListProps={{ 'aria-label': t('common.openInNewTab') }}
      >
        <MenuItem
          onClick={handleOpenInNewTab}
          dense
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('common.openInNewTab')} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SidebarContent;
